import { FC, useMemo, useState } from 'react';
import * as z from 'zod';
import {
  Alert,
  Button,
  Stack,
  Text,
  SIZE,
  List,
  Switch,
  Modal,
  Form,
  useForm,
} from '@quinyx/ui';

import {
  useGetModules,
  useEditModule,
  useGetNEOModules,
  useEditNEOModule,
} from '../../hooks/modules';
import { MODULE_TITLE_MAP, IModule } from '@/api/interfaces/customer';
import { toISOFormat, format } from '../../utils/time';
import { useTranslate } from '../../hooks/translate';

type ModuleProps = {
  customerId: string;
  isDisabled?: boolean;
};

const dateSchema = z.object({
  startDate: z.string().optional().nullable(),
  endDate: z.string().optional().nullable(),
});

const formatDateForInput = (dateString?: string | null) => {
  if (!dateString) return undefined;
  return format(dateString, 'yyyy-MM-dd');
};

const sortModules = (modules: IModule[]) => {
  return modules
    .slice()
    .sort((a: IModule, b: IModule) => a.moduleName.localeCompare(b.moduleName));
};

const ModulesList: FC<ModuleProps> = ({ customerId, isDisabled = false }) => {
  const { data: mobileModules } = useGetModules(customerId);
  const { data: neoModules } = useGetNEOModules(customerId);
  const { t } = useTranslate();

  const { updateModule, isPending: isModulePending } = useEditModule();
  const { updateNEOModule, isPending: isNEOModulePending } = useEditNEOModule();

  const [selectedModule, setSelectedModule] = useState<IModule | null>(null);

  const formMethods = useForm({
    schema: dateSchema,
    defaultValues: {
      startDate: undefined,
      endDate: undefined,
    },
  });

  const sortedMobileModules = useMemo(() => {
    return sortModules(mobileModules ?? []);
  }, [mobileModules]);
  const sortedNEOModules = useMemo(() => {
    return sortModules(neoModules ?? []);
  }, [neoModules]);

  const handleOnModalSubmit = (values: z.infer<typeof dateSchema>) => {
    if (!selectedModule) return;
    const { enabled, ...editedModule } = {
      ...selectedModule,
      startDate: values.startDate || undefined,
      endDate: values.endDate || undefined,
    };

    if (selectedModule.groupId) {
      updateModule({
        domainId: customerId,
        payload: editedModule,
      });
    } else {
      const stateChanged = enabled !== !!values.startDate;

      updateNEOModule({
        domainId: customerId,
        payload: {
          ...editedModule,
          enable: stateChanged ? !!values.startDate : undefined,
        },
      });
    }

    setSelectedModule(null);
    formMethods.reset();
  };

  const handleOnSwitchChange = (module: IModule, checked: boolean) => {
    if (module.groupId) {
      // Only mobile modules have a groupId
      updateModule({
        domainId: customerId,
        payload: {
          ...module,
          startDate: checked ? toISOFormat(new Date()) : undefined,
          endDate: checked ? module.endDate : undefined,
        },
      });
    } else {
      updateNEOModule({
        domainId: customerId,
        payload: { ...module, enable: checked },
      });
    }
  };

  return (
    <Stack spacing={SIZE.M}>
      <Alert type="info">
        <Text variant="smallRegular">{t('modulesForm.infoAlert')}</Text>
      </Alert>

      <Stack spacing={SIZE.XS}>
        <Text variant="headlineHeavy">{t('modulesForm.neoModules')}</Text>
        <Text variant="smallRegular" color="SurfaceOnSurfaceAlt">
          {t('modulesForm.neoModulesDescription')}
        </Text>
      </Stack>

      <List>
        {sortedNEOModules?.map((module: IModule) => (
          <List.Item key={module.moduleName}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center">
              <List.ItemText label={t(`modules.${module.moduleName}`)} />
              <Stack
                direction="row"
                spacing={SIZE.S}
                fullWidth={false}
                alignItems="center">
                {!!module.startDate && (
                  <Stack
                    direction="row"
                    spacing={SIZE.S}
                    className="white-space-nowrap">
                    <Text variant="xSmallRegular" color="SurfaceOnSurfaceAlt">
                      {format(module.startDate, 'P')}
                      {module.endDate && ` - ${format(module.endDate, 'P')}`}
                    </Text>
                  </Stack>
                )}
                <Switch
                  data-test-id={`module-switch-${module.moduleName}`}
                  checked={!!module.startDate}
                  disabled={isNEOModulePending || isDisabled}
                  onChange={(checked) => {
                    handleOnSwitchChange(module, checked.target.value);
                  }}
                />

                <Button
                  data-test-id="module-edit-button"
                  text={t('common.edit')}
                  leftIcon="edit"
                  intent="utility"
                  variant="outlined"
                  size={SIZE.S}
                  disabled={isDisabled}
                  onClick={() => {
                    setSelectedModule(module);
                    formMethods.reset({
                      startDate: formatDateForInput(module.startDate),
                      endDate: formatDateForInput(module.endDate),
                    });
                  }}
                />
              </Stack>
            </Stack>
          </List.Item>
        ))}
      </List>

      <Stack spacing={SIZE.XS} mt={SIZE.L}>
        <Text variant="headlineHeavy">{t('modulesForm.mobileModules')}</Text>
        <Text variant="smallRegular" color="SurfaceOnSurfaceAlt">
          {t('modulesForm.mobileModulesDescription')}
        </Text>
      </Stack>

      <List>
        {sortedMobileModules?.map((module: IModule) => (
          <List.Item key={module.moduleName}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center">
              <List.ItemText
                label={t(`modules.${module.moduleName}`, {
                  defaultValue: MODULE_TITLE_MAP[module.moduleName],
                })}
              />
              <Stack
                direction="row"
                spacing={SIZE.S}
                fullWidth={false}
                alignItems="center">
                {!!module.startDate && (
                  <Stack
                    direction="row"
                    spacing={SIZE.S}
                    className="white-space-nowrap">
                    <Text variant="xSmallRegular" color="SurfaceOnSurfaceAlt">
                      {format(module.startDate, 'P')}
                      {module.endDate && ` - ${format(module.endDate, 'P')}`}
                    </Text>
                  </Stack>
                )}
                <Switch
                  data-test-id={`module-switch-${module.moduleName}`}
                  checked={!!module.startDate}
                  disabled={isModulePending || isDisabled}
                  onChange={(checked) => {
                    handleOnSwitchChange(module, checked.target.value);
                  }}
                />

                <Button
                  data-test-id="module-edit-button"
                  text={t('common.edit')}
                  leftIcon="edit"
                  intent="utility"
                  variant="outlined"
                  size={SIZE.S}
                  disabled={isDisabled}
                  onClick={() => {
                    setSelectedModule(module);
                    formMethods.reset({
                      startDate: formatDateForInput(module.startDate),
                      endDate: formatDateForInput(module.endDate),
                    });
                  }}
                />
              </Stack>
            </Stack>
          </List.Item>
        ))}
      </List>

      <Modal
        open={!!selectedModule}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedModule(null);
            formMethods.reset();
          }
        }}>
        <Modal.Header>
          <Modal.CloseButton ariaLabel="close-modal" dataTestId="close-modal" />
        </Modal.Header>
        <Modal.Content>
          <Modal.HeadlineText>{t('modulesForm.editModule')}</Modal.HeadlineText>
          <Form methods={formMethods} onSubmit={handleOnModalSubmit}>
            <Stack spacing={SIZE.M}>
              <Form.TextInput
                name="startDate"
                label={t('modulesForm.startDate')}
                type="date"
                data-test-id="module-start-date-picker"
              />
              <Form.TextInput
                name="endDate"
                label={t('modulesForm.endDate')}
                type="date"
                info={t('modulesForm.endDateInfo')}
                data-test-id="module-end-date-picker"
              />
              <Stack direction="row" spacing={SIZE.S} justifyContent="flex-end">
                <Button
                  data-test-id="cancel-module-button"
                  text={t('common.cancel')}
                  variant="text"
                  onClick={() => {
                    setSelectedModule(null);
                    formMethods.reset();
                  }}
                />
                <Form.SubmitButton
                  data-test-id="save-module-button"
                  text={t('common.save')}
                  disabled={isDisabled}
                />
              </Stack>
            </Stack>
          </Form>
        </Modal.Content>
      </Modal>
    </Stack>
  );
};

export default ModulesList;
